/**
 * Deep-link return after sign-in (FE-UI-11).
 *
 * ProtectedRoute sends a signed-out visitor to /login with the page they asked
 * for in `state.from`; after sign-in, App and Login send them back there.
 * History state is writable by any script on the page, so the value is
 * treated as untrusted: only a same-origin, root-relative path is honoured,
 * and anything else lands on /reports.
 */

export const DEFAULT_RETURN_PATH = '/reports';

/** Pages that would bounce straight back to the sign-in form. */
const NO_RETURN_PATHS = new Set(['/login', '/signup']);

interface PathParts {
  pathname: string;
  search?: string;
  hash?: string;
}

export interface LoginRedirectState {
  from: { pathname: string; search: string; hash: string };
}

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

/**
 * A single leading slash, not followed by another slash or a backslash (both
 * of which browsers read as the start of a host), and no control characters.
 */
function isSameOriginPath(pathname: string): boolean {
  return (
    pathname.startsWith('/') &&
    !pathname.startsWith('//') &&
    !pathname.startsWith('/\\') &&
    !CONTROL_CHARS.test(pathname)
  );
}

function optionalPart(value: unknown, prefix: string): string | null {
  if (value === undefined || value === '') return '';
  if (typeof value !== 'string' || !value.startsWith(prefix) || CONTROL_CHARS.test(value)) {
    return null;
  }
  return value;
}

/** The path to open after sign-in, from the login page's location state. */
export function getReturnPath(state: unknown): string {
  if (typeof state !== 'object' || state === null) return DEFAULT_RETURN_PATH;
  const from = (state as { from?: unknown }).from;
  if (typeof from !== 'object' || from === null) return DEFAULT_RETURN_PATH;

  const { pathname, search, hash } = from as Record<string, unknown>;
  if (typeof pathname !== 'string' || !isSameOriginPath(pathname)) return DEFAULT_RETURN_PATH;
  if (NO_RETURN_PATHS.has(pathname)) return DEFAULT_RETURN_PATH;

  const searchPart = optionalPart(search, '?');
  const hashPart = optionalPart(hash, '#');
  if (searchPart === null || hashPart === null) return DEFAULT_RETURN_PATH;

  return `${pathname}${searchPart}${hashPart}`;
}

/**
 * The state ProtectedRoute puts on its /login redirect. Only the path parts
 * are kept: the page's own history state (which may carry report data) never
 * rides along.
 */
export function loginRedirectState(location: PathParts): LoginRedirectState {
  return {
    from: {
      pathname: location.pathname,
      search: location.search ?? '',
      hash: location.hash ?? '',
    },
  };
}
